import { useMemo } from 'react';

import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
// Hooks
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { getAnalyticsGroupUsedNetworkTypes } from '@/utils/analytics/getAnalyticsGroupUsedNetworkTypes';
// Utils
import faqUrl from '@/utils/urls/faqUrl';

// Lib
import { INSTAGRAM } from '@/lib/socialProfile';
import { url_faq_estimated_followers } from '@/lib/urls/faqUrls';

// Components
import LinkBase from '@/components/base/LinkBase';
import NoDataNetworksList from '@/components/composite/Analyze/NoDataNetworksList';
import { Trans } from '@/components/tools/translation';

const NoDataChart = ({ data, isExport, exportData }) => {
    const { t } = useTranslation('analytics');
    const { socialProfiles } = useSocialProfiles();
    const { activeSocialProfile } = useActiveSocialProfile();
    const { spGroups } = useSPGroups();

    const socialNetworkTypes = useMemo(() => {
        if (data.groups && data.aggregatedNetworks && !isExport) {
            return getAnalyticsGroupUsedNetworkTypes(
                spGroups?.find((group) => group.id === data.groups[0]),
                data.aggregatedNetworks
            );
        } else if (data.social_profiles && !isExport) {
            const socialProfile = socialProfiles?.find((sp) => sp.id === data.social_profiles[0]);
            return [socialProfile?.social_network_type];
        } else if (isExport) {
            return [exportData.social_profile?.social_network_type];
        }

        return [activeSocialProfile?.social_network_type ?? INSTAGRAM];
    }, [
        activeSocialProfile?.social_network_type,
        data.groups,
        data.aggregatedNetworks,
        data.social_profiles,
        exportData,
        isExport,
        socialProfiles,
        spGroups
    ]);

    const getMessage = () => {
        if (data?.no_first_media) {
            return t('no_first_media');
        }
        if (data.noDataToken) {
            return (
                <Trans
                    i18nKey={`analytics:${data.noDataToken}`}
                    components={{
                        tag0: (
                            <LinkBase
                                key="learn_more"
                                className="underline"
                                target="_blank"
                                href={faqUrl(url_faq_estimated_followers)}
                                rel="help noopener noreferrer"
                            />
                        )
                    }}
                />
            );
        }
        return t('no_data_period');
    };

    return (
        <NoDataNetworksList
            socialNetworkTypes={socialNetworkTypes}
            isExport={isExport}
            className={isExport ? 'min-h-[140px]' : 'min-h-[270px]'}>
            {getMessage()}
        </NoDataNetworksList>
    );
};

export default NoDataChart;
